/** @jsxImportSource theme-ui */
import React from 'react';
import { ResponsiveStyleValue } from 'theme-ui';
import { SpaceSizing } from '../utils/types';
import { useBreakpointValue } from '../../hooks';
import { CarrotTheme } from '../../theme';

interface UseSpacingStylesProps {
    spacing?: ResponsiveStyleValue<SpaceSizing>;
    spacingX?: ResponsiveStyleValue<SpaceSizing>;
    spacingY?: ResponsiveStyleValue<SpaceSizing>;
}
interface UseColumnStylesProps {
    columns?: ResponsiveStyleValue<number | 'auto-fit'>;
    minChildWidth?: ResponsiveStyleValue<string>;
}

export interface SimpleGridProps extends UseSpacingStylesProps, UseColumnStylesProps {
    height?: ResponsiveStyleValue<string>;
}

function useSpacingStyles({ spacing, spacingX, spacingY }: UseSpacingStylesProps) {
    const spacingRow = spacingX || spacing;
    const spacingColumn = spacingY || spacing;

    const rowResponsive = useBreakpointValue(spacingRow);
    const columnResponsive = useBreakpointValue(spacingColumn);

    return {
        gap: (theme: CarrotTheme) =>
            // eslint-disable-next-line
            // @ts-ignore
            `${theme.space[columnResponsive] ?? columnResponsive} ${theme.space[rowResponsive] ?? rowResponsive}`,
    };
}

function useColumnStyles({ minChildWidth, columns }: UseColumnStylesProps) {
    const responsiveColumns = useBreakpointValue(columns);
    const responsiveMinChildWidth = useBreakpointValue(minChildWidth);
    return {
        gridTemplateColumns: `repeat(${responsiveColumns}, minmax(${responsiveMinChildWidth}, 1fr))`,
    };
}
export function SimpleGrid({
    spacing,
    spacingX,
    spacingY,
    minChildWidth,
    columns,
    height,
    ...otherProps
}: React.PropsWithChildren<SimpleGridProps>): JSX.Element {
    const spacingStyles = useSpacingStyles({ spacing, spacingX, spacingY });
    const columnStyles = useColumnStyles({ minChildWidth, columns });
    return (
        <div
            sx={{
                display: 'grid',
                height,
                ...spacingStyles,
                ...columnStyles,
            }}
            {...otherProps}
        />
    );
}

SimpleGrid.defaultProps = {
    columns: 'auto-fit',
    minChildWidth: '0px',
    spacing: 'medium',
};
